<template>
    <div>
        <b-modal
            id="createWorkTypeModal"
            :visible="show"
            title="Создание вида работ"
            ok-title="Создать"
            ok-variant="success"
            cancel-title="Отмена"
            cancel-variant="secondary"
            :busy="saving"
            :ok-disabled="saving || !formValid"
            no-close-on-backdrop
            @ok="submit"
            @hidden="onHidden"
        >
            <b-alert v-if="error" show variant="danger">
                {{ error }}
            </b-alert>

            <b-form-group label="Название работы" label-for="create-work-type-name">
                <b-form-input
                    id="create-work-type-name"
                    v-model.trim="name"
                    placeholder="Например, ремонт швов"
                    size="sm"
                    :disabled="saving"
                    :state="name ? true : null"
                ></b-form-input>
            </b-form-group>

            <b-form-group label="Единица измерения" label-for="create-work-type-measurement">
                <v-select
                    id="create-work-type-measurement"
                    v-model="measurement"
                    :options="measurementOptions"
                    :disabled="saving || workTypesLoading"
                    :clearable="false"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                    :placeholder="workTypesLoading ? 'Загружаем единицы…' : 'Выберите единицу'"
                >
                    <template #no-options>Единицы измерения не найдены</template>
                </v-select>
            </b-form-group>

            <b-form-group label="Группа работ" label-for="create-work-type-group">
                <v-select
                    id="create-work-type-group"
                    v-model="groupId"
                    :options="workGroups"
                    :reduce="group => group.id"
                    label="name"
                    :disabled="saving"
                    :clearable="true"
                    append-to-body
                    :calculate-position="positionModalSelectDropdown"
                    :filter="(options, search) => searchText(options, search, ['name'])"
                    placeholder="Без группы"
                ></v-select>
            </b-form-group>

            <b-button
                v-if="$can('create', 'workGroups')"
                variant="outline-info"
                size="sm"
                :disabled="saving"
                @click="showCreateWorkGroup"
            >
                <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                Создать группу работ
            </b-button>
        </b-modal>

        <CreateWorkGroup
            :show="createWorkGroupVisible"
            @hidden="hideCreateWorkGroup"
            @workGroupCreated="onWorkGroupCreated"
        />
    </div>
</template>

<script>
import CreateWorkGroup from '@/components/CreateWorkGroup/CreateWorkGroup.vue';
import searchText from '@/utils/searchText';
import {positionVueSelectDropdown} from '@/utils/vueSelectPositioning';
import {getWorkTypeMeasurementOptions} from '@/utils/workTypeMeasurements';

export default {
    name: 'CreateWorkType',
    components: {
        CreateWorkGroup
    },
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            name: '',
            measurement: '',
            groupId: null,
            saving: false,
            error: '',
            workTypesLoading: false,
            workTypesLoadId: 0,
            createWorkGroupVisible: false
        };
    },
    computed: {
        workGroups() {
            return Array.isArray(this.$store.state.workGroups)
                ? this.$store.state.workGroups
                : [];
        },
        workTypes() {
            return Array.isArray(this.$store.state.workTypes)
                ? this.$store.state.workTypes
                : [];
        },
        measurementOptions() {
            return getWorkTypeMeasurementOptions(this.workTypes);
        },
        normalizedMeasurement() {
            return String(this.measurement || '').trim();
        },
        formValid() {
            return Boolean(this.name.trim() && this.normalizedMeasurement);
        }
    },
    watch: {
        show(value) {
            if (value) {
                this.loadWorkGroups();
                this.loadWorkTypes();
            }
        }
    },
    methods: {
        searchText,
        positionModalSelectDropdown: positionVueSelectDropdown,
        loadWorkGroups() {
            if (this.workGroups.length) return;
            this.$store.dispatch('getWorkGroups').catch(error => {
                this.error = this.errorText(error, 'Не удалось загрузить группы работ');
            });
        },
        async loadWorkTypes() {
            const loadId = ++this.workTypesLoadId;
            this.workTypesLoading = true;
            try {
                await this.$store.dispatch('getWorkTypes');
            } catch (error) {
                if (loadId === this.workTypesLoadId) {
                    this.error = this.errorText(error, 'Не удалось загрузить единицы измерения');
                }
            } finally {
                if (loadId === this.workTypesLoadId) {
                    this.workTypesLoading = false;
                }
            }
        },
        clearForm() {
            this.workTypesLoadId += 1;
            this.name = '';
            this.measurement = '';
            this.groupId = null;
            this.saving = false;
            this.error = '';
            this.workTypesLoading = false;
            this.createWorkGroupVisible = false;
        },
        onHidden() {
            this.clearForm();
            this.$emit('hidden');
        },
        showCreateWorkGroup() {
            this.createWorkGroupVisible = true;
        },
        hideCreateWorkGroup() {
            this.createWorkGroupVisible = false;
        },
        async onWorkGroupCreated(createdGroupId) {
            try {
                await this.$store.dispatch('getWorkGroups');
                this.groupId = createdGroupId;
                this.hideCreateWorkGroup();
            } catch (error) {
                this.error = this.errorText(error, 'Не удалось обновить группы работ');
            }
        },
        resolveCreatedWorkType(response) {
            const created = response && response.data ? response.data : (response || {});
            const createdId = Number(created.work_id || created.id);
            if (createdId) {
                const option = this.workTypes.find(item => Number(item.work_id) === createdId);
                if (option) return option;
            }

            return this.workTypes.find(item =>
                String(item.name || '').trim() === this.name.trim() &&
                String(item.unit_of_measurement || '').trim() === this.normalizedMeasurement
            ) || created;
        },
        async submit(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.formValid || this.saving) return;

            this.saving = true;
            this.error = '';
            try {
                const response = await this.$store.dispatch('createWorkType', {
                    name: this.name.trim(),
                    unit_of_measurement: this.normalizedMeasurement,
                    group_id: this.groupId
                });
                await this.$store.dispatch('getWorkTypes');
                const createdWorkType = this.resolveCreatedWorkType(response);
                this.$emit('created', createdWorkType);
                this.$bvModal.hide('createWorkTypeModal');
            } catch (error) {
                this.error = this.errorText(error, 'Не удалось создать вид работ');
            } finally {
                this.saving = false;
            }
        },
        errorText(error, fallback) {
            if (error && error.response && error.response.data) {
                const data = error.response.data;
                if (typeof data === 'string') return data;
                if (data && data.msg) return data.msg;
            }
            return fallback;
        }
    }
};
</script>
